import { useObserver } from 'mobx-react-lite';
import { ApolloError } from 'apollo-client';

import PublicEnv from 'shared/utils/public-env';

import { useStartAggregatorLinkV2Query } from 'types/graphql';
import { useStores } from 'contexts/stores';
import useDutchiePayEnrollment from 'src/hooks/use-dutchiePay-enrollment';
import { AGGREGATORS } from 'src/payments/constants';

import useErnie from 'shared/hooks/use-ernie';
import { formatPaymentsError } from 'src/payments/hooks/use-payments-error/use-payments-error';

type UseStartAeroSyncConnectionResponse = {
  token: string;
  configurationId: string;
  aeroPassUserUuid: string;
  loading: boolean;
  error: ApolloError | undefined;
};

export const useStartAeroSyncConnection = ({
  brandedDutchiePayName,
}: {
  brandedDutchiePayName: string;
}): UseStartAeroSyncConnectionResponse => {
  const showErnie = useErnie();
  const { aggregatorLinkExited } = useDutchiePayEnrollment();

  const { User } = useStores();
  const userFirstName = useObserver(() => User.profile.firstName);
  const userLastName = useObserver(() => User.profile.lastName);
  const userEmail = useObserver(() => User.email);
  const userPhone = useObserver(() => User.profile.phone);
  const { data, error, loading } = useStartAggregatorLinkV2Query({
    variables: {
      aggregator: AGGREGATORS.AEROSYNC,
      firstName: userFirstName,
      lastName: userLastName,
      email: userEmail,
      phone: userPhone,
    },
    skip: !userFirstName || !userLastName || !userEmail || !userPhone,
    fetchPolicy: 'network-only',
  });

  const { token = '', userId = '' } = data?.startAggregatorLinkV2.aerosync ?? {};

  if (error) {
    const { errorMarkdown, ernieType, ernieTimeout } = formatPaymentsError(error, brandedDutchiePayName);
    showErnie(errorMarkdown, ernieType, ernieTimeout);
    aggregatorLinkExited();
  }

  return {
    token,
    configurationId: PublicEnv.aerosyncConfigurationId,
    aeroPassUserUuid: userId,
    loading,
    error,
  };
};
